import { useCallback, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { useClient } from 'urql';
import { pipe, subscribe } from 'wonka';

import type {
    IUserLogoutSubscription,
    IUserLogoutSubscriptionVariables,
} from '../graphql/authentication.graphql';
import { UserLogoutDocument } from '../graphql/authentication.graphql';

import { useIdle } from './use-idle';

export function useAuthController() {
    const auth = useAuth();
    const userProfileId = auth.user?.profile.user_profile_id as string;
    const sessionId = auth.user?.profile.sid;
    const client = useClient();

    useEffect(() => {
        if (!auth.user) {
            return;
        }

        if (auth.user.expired && !auth.isLoading) {
            console.log('[AuthController]', 'Auth Expired, signing back in...');
            auth.signinSilent().catch((e) => {
                console.log('[AuthController]', 'Signing out due to error signing in...', e);
                auth.signoutSilent();
            });
            return;
        }
    }, [auth]);

    useIdle(
        30000,
        useCallback(
            (isIdle: boolean) => {
                if (!isIdle) {
                    console.log('[AuthController]', 'Starting silent renew as user has returned');
                    auth.startSilentRenew();
                    return;
                }

                console.log('[AuthController]', 'Stopping silent renew as user is idle');
                auth.stopSilentRenew();
            },
            [auth],
        ),
    );

    useEffect(() => {
        if (!userProfileId || !sessionId) {
            return;
        }

        const subscription = pipe(
            client.subscription<IUserLogoutSubscription, IUserLogoutSubscriptionVariables>(
                UserLogoutDocument,
                {
                    userProfileId,
                    sessionId,
                },
            ),
            subscribe(() => {
                console.log('[AuthController]', 'Removing user');
                auth.removeUser();
            }),
        );

        return () => subscription.unsubscribe();
    }, [userProfileId, sessionId, auth, client]);
}
